import { SEARCH_TILE_SIZE_MIN, SEARCH_TILE_SIZE_DEFAULT, SEARCH_TILE_SIZE_MAX, SEARCH_TILE_SIZE_STEP } from '../features/search-workspace/tileSize';
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
  apiService,
  Index,
  PreviewGrantItem,
  PreviewGrantRequestItem,
  Prompt,
  PromptRun,
  Video,
} from '../services/api';
import { retrySafeRead } from '../services/safeRead';
import { PreviewGrantProvider, usePreviewGrantClient } from './PreviewGrantContext';
import type { PreviewGrantProjection } from './PreviewGrantContext';
export { SEARCH_TILE_SIZE_MIN, SEARCH_TILE_SIZE_DEFAULT, SEARCH_TILE_SIZE_MAX, SEARCH_TILE_SIZE_STEP } from '../features/search-workspace/tileSize';
export { getPreviewGrantIdentityKey, getPreviewGrantProjectionKey, PREVIEW_GRANT_NEGATIVE_TTL_MS } from './PreviewGrantContext';
export type { PreviewGrantProjection } from './PreviewGrantContext';

const loadPreviewGrants = (items: PreviewGrantRequestItem[], signal: AbortSignal) => retrySafeRead(() => {
  if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
  return apiService.createPreviewGrants(items, signal);
});

export type SearchWorkspacePanel = 'search' | 'agent';
export type SearchWorkspaceView = 'segments' | 'videos';
export type SearchWorkspaceTileSize = number;
// `sql` is retained only to migrate an already-open workspace back to Search.
export type SearchWorkspaceCanvas = 'browse' | 'search' | 'sql';

export interface SearchRuntimeCatalogSnapshot {
  indexes: Index[] | null;
  runsByIndex: Record<string, PromptRun[]>;
  promptsById: Record<string, Prompt>;
}

export interface SearchRuntimeCatalogPatch {
  indexes?: Index[];
  runsByIndex?: Record<string, PromptRun[]>;
  promptsById?: Record<string, Prompt>;
}

interface SearchWorkspaceContextValue {
  panel: SearchWorkspacePanel;
  setPanel: (panel: SearchWorkspacePanel) => void;
  view: SearchWorkspaceView;
  setView: (view: SearchWorkspaceView) => void;
  tileSize: SearchWorkspaceTileSize;
  setTileSize: (tileSize: SearchWorkspaceTileSize) => void;
  searchCanvas: SearchWorkspaceCanvas;
  setSearchCanvas: (canvas: SearchWorkspaceCanvas) => void;
  getScrollPosition: (canvasKey: string) => number;
  setScrollPosition: (canvasKey: string, scrollTop: number) => void;
  getVideoSummary: (videoId: string) => Video | null;
  getVideoSummaries: (
    videoIds?: Iterable<string>,
    options?: { includeExpired?: boolean },
  ) => Video[];
  primeVideoSummaries: (
    videos: Video[],
    options?: {
      mediaOnly?: boolean;
      expectedThumbnailGcsUris?: ReadonlyMap<string, string | null>;
      expectedGifGcsUris?: ReadonlyMap<string, string | null>;
    },
  ) => void;
  isVideoSummaryMissing: (videoId: string) => boolean;
  markVideoSummariesMissing: (videoIds: string[]) => void;
  getSearchRuntimeCatalog: () => SearchRuntimeCatalogSnapshot;
  primeSearchRuntimeCatalog: (patch: SearchRuntimeCatalogPatch) => void;
  requestPreviewGrant: (
    projection: PreviewGrantProjection,
    options?: { forceRefresh?: boolean },
  ) => Promise<PreviewGrantItem | null>;
}

const SearchWorkspaceContext = createContext<SearchWorkspaceContextValue | null>(null);

const MAX_SCROLL_POSITIONS = 120;
const MAX_VIDEO_SUMMARIES = 2000;
const MAX_SEARCH_RUN_CATALOGS = 40;
const MAX_SEARCH_PROMPT_SCHEMAS = 2000;
const TILE_SIZE_STORAGE_KEY = 'searchWorkspaceTileSize';

const LEGACY_TILE_SIZES: Record<string, SearchWorkspaceTileSize> = {
  small: 160,
  medium: SEARCH_TILE_SIZE_DEFAULT,
  large: 320,
};

const normalizeTileSize = (value: unknown): SearchWorkspaceTileSize => {
  const legacySize = typeof value === 'string' ? LEGACY_TILE_SIZES[value] : undefined;
  const numericSize = legacySize ?? (
    typeof value === 'number'
      ? value
      : typeof value === 'string' && value.trim()
        ? Number(value)
        : Number.NaN
  );
  if (!Number.isFinite(numericSize)) return SEARCH_TILE_SIZE_DEFAULT;
  const clampedSize = Math.min(SEARCH_TILE_SIZE_MAX, Math.max(SEARCH_TILE_SIZE_MIN, numericSize));
  const snappedSize = SEARCH_TILE_SIZE_MIN + Math.round(
    (clampedSize - SEARCH_TILE_SIZE_MIN) / SEARCH_TILE_SIZE_STEP,
  ) * SEARCH_TILE_SIZE_STEP;
  return Math.min(SEARCH_TILE_SIZE_MAX, Math.max(SEARCH_TILE_SIZE_MIN, snappedSize));
};

const getInitialTileSize = (): SearchWorkspaceTileSize => {
  if (typeof window === 'undefined') return SEARCH_TILE_SIZE_DEFAULT;
  try {
    const stored = window.localStorage.getItem(TILE_SIZE_STORAGE_KEY);
    const normalizedSize = normalizeTileSize(stored);
    if (stored !== null && stored !== String(normalizedSize)) {
      window.localStorage.setItem(TILE_SIZE_STORAGE_KEY, String(normalizedSize));
    }
    return normalizedSize;
  } catch {
    return SEARCH_TILE_SIZE_DEFAULT;
  }
};

const trimOldestEntries = <Key, Value>(map: Map<Key, Value>, maximum: number) => {
  while (map.size > maximum) {
    const oldestKey = map.keys().next().value as Key | undefined;
    if (oldestKey === undefined) return;
    map.delete(oldestKey);
  }
};

export const SearchWorkspaceProvider: React.FC<{
  children: ReactNode;
  userId?: string | null;
}> = ({ children, userId = null }) => {
  const [panel, setPanel] = useState<SearchWorkspacePanel>('search');
  const [view, setView] = useState<SearchWorkspaceView>('segments');
  const [tileSize, setTileSizeState] = useState<SearchWorkspaceTileSize>(getInitialTileSize);
  // This provider lives above the route-level tab switch. Keep canvas
  // ownership here so returning to Search restores the exact last dataset.
  const [searchCanvas, setSearchCanvas] = useState<SearchWorkspaceCanvas>('browse');
  const scrollPositionsRef = useRef(new Map<string, number>());
  const videoSummariesRef = useRef(new Map<string, Video>());
  const missingVideoSummariesRef = useRef(new Set<string>());
  const searchIndexesRef = useRef<Index[] | null>(null);
  const searchRunsByIndexRef = useRef(new Map<string, PromptRun[]>());
  const searchPromptsByIdRef = useRef(new Map<string, Prompt>());
  const authScope = typeof userId === 'string' && userId.trim() ? userId.trim() : '__signed_out__';
  const requestPreviewGrant = usePreviewGrantClient(authScope, loadPreviewGrants);

  const setTileSize = useCallback((nextTileSize: SearchWorkspaceTileSize) => {
    const normalizedSize = normalizeTileSize(nextTileSize);
    setTileSizeState(normalizedSize);
    try {
      window.localStorage.setItem(TILE_SIZE_STORAGE_KEY, String(normalizedSize));
    } catch {
      // Preference persistence is best effort; layout state still updates in memory.
    }
  }, []);

  const getScrollPosition = useCallback((canvasKey: string) => (
    scrollPositionsRef.current.get(canvasKey) || 0
  ), []);

  const setScrollPosition = useCallback((canvasKey: string, scrollTop: number) => {
    scrollPositionsRef.current.delete(canvasKey);
    scrollPositionsRef.current.set(canvasKey, Math.max(0, scrollTop));
    trimOldestEntries(scrollPositionsRef.current, MAX_SCROLL_POSITIONS);
  }, []);

  const getVideoSummary = useCallback((videoId: string) => {
    const summary = videoSummariesRef.current.get(videoId) || null;
    if (summary) {
      videoSummariesRef.current.delete(videoId);
      videoSummariesRef.current.set(videoId, summary);
    }
    return summary;
  }, []);

  const getVideoSummaries = useCallback((
    videoIds?: Iterable<string>,
    _options: { includeExpired?: boolean } = {},
  ) => {
    const requestedIds = videoIds
      ? Array.from(videoIds)
      : Array.from(videoSummariesRef.current.keys());
    return requestedIds.flatMap((videoId) => {
      const summary = videoSummariesRef.current.get(videoId);
      return summary ? [summary] : [];
    });
  }, []);

  const primeVideoSummaries = useCallback((
    videos: Video[],
    options: {
      mediaOnly?: boolean;
      expectedThumbnailGcsUris?: ReadonlyMap<string, string | null>;
      expectedGifGcsUris?: ReadonlyMap<string, string | null>;
    } = {},
  ) => {
    videos.forEach((video) => {
      if (!video?.video_id) return;
      const previous = videoSummariesRef.current.get(video.video_id);
      if (
        options.mediaOnly
        && options.expectedThumbnailGcsUris?.has(video.video_id)
      ) {
        const expectedThumbnailGcsUri = options.expectedThumbnailGcsUris.get(video.video_id) ?? null;
        const currentThumbnailGcsUri = previous?.thumbnail_gcs_uri || null;
        if (currentThumbnailGcsUri !== expectedThumbnailGcsUri) return;
      }
      if (options.mediaOnly && options.expectedGifGcsUris?.has(video.video_id)) {
        const expectedGifGcsUri = options.expectedGifGcsUris.get(video.video_id) ?? null;
        if ((previous?.gif_gcs_uri || null) !== expectedGifGcsUri) return;
      }
      const nextVideo = options.mediaOnly && previous
        ? {
            ...previous,
            thumbnail_gcs_uri: video.thumbnail_gcs_uri ?? null,
            thumbnail_uri: null,
            thumbnail_available: Boolean(video.thumbnail_gcs_uri),
            gif_gcs_uri: video.gif_gcs_uri ?? null,
            gif_uri: null,
            gif_available: Boolean(video.gif_gcs_uri),
          }
        : previous
          ? {
              ...previous,
              ...video,
              thumbnail_uri: null,
              thumbnail_available: Boolean(video.thumbnail_gcs_uri),
              gif_uri: null,
              gif_available: Boolean(video.gif_gcs_uri),
            }
          : {
              ...video,
              thumbnail_uri: null,
              thumbnail_available: Boolean(video.thumbnail_gcs_uri),
              gif_uri: null,
              gif_available: Boolean(video.gif_gcs_uri),
            };
      missingVideoSummariesRef.current.delete(video.video_id);
      videoSummariesRef.current.delete(video.video_id);
      videoSummariesRef.current.set(video.video_id, nextVideo);
    });
    trimOldestEntries(videoSummariesRef.current, MAX_VIDEO_SUMMARIES);
  }, []);

  const isVideoSummaryMissing = useCallback(
    (videoId: string) => missingVideoSummariesRef.current.has(videoId),
    [],
  );

  const markVideoSummariesMissing = useCallback((videoIds: string[]) => {
    videoIds.forEach((videoId) => {
      if (!videoId || videoSummariesRef.current.has(videoId)) return;
      missingVideoSummariesRef.current.delete(videoId);
      missingVideoSummariesRef.current.add(videoId);
    });
    while (missingVideoSummariesRef.current.size > MAX_VIDEO_SUMMARIES) {
      const oldestKey = missingVideoSummariesRef.current.values().next().value as string | undefined;
      if (oldestKey === undefined) break;
      missingVideoSummariesRef.current.delete(oldestKey);
    }
  }, []);

  const getSearchRuntimeCatalog = useCallback((): SearchRuntimeCatalogSnapshot => ({
    indexes: searchIndexesRef.current ? [...searchIndexesRef.current] : null,
    runsByIndex: Object.fromEntries(
      Array.from(searchRunsByIndexRef.current.entries()).map(([indexId, runs]) => (
        [indexId, [...runs]]
      )),
    ),
    promptsById: Object.fromEntries(searchPromptsByIdRef.current.entries()),
  }), []);

  const primeSearchRuntimeCatalog = useCallback((patch: SearchRuntimeCatalogPatch) => {
    if (patch.indexes !== undefined) {
      searchIndexesRef.current = [...patch.indexes];
      const activeIndexIds = new Set(patch.indexes.map((index) => index.index_id));
      Array.from(searchRunsByIndexRef.current.keys()).forEach((indexId) => {
        if (!activeIndexIds.has(indexId)) searchRunsByIndexRef.current.delete(indexId);
      });
    }
    Object.entries(patch.runsByIndex || {}).forEach(([indexId, runs]) => {
      searchRunsByIndexRef.current.delete(indexId);
      searchRunsByIndexRef.current.set(indexId, [...runs]);
    });
    trimOldestEntries(searchRunsByIndexRef.current, MAX_SEARCH_RUN_CATALOGS);
    Object.entries(patch.promptsById || {}).forEach(([promptId, prompt]) => {
      searchPromptsByIdRef.current.delete(promptId);
      searchPromptsByIdRef.current.set(promptId, prompt);
    });
    trimOldestEntries(searchPromptsByIdRef.current, MAX_SEARCH_PROMPT_SCHEMAS);
  }, []);

  const value = useMemo<SearchWorkspaceContextValue>(() => ({
    panel,
    setPanel,
    view,
    setView,
    tileSize,
    setTileSize,
    searchCanvas,
    setSearchCanvas,
    getScrollPosition,
    setScrollPosition,
    getVideoSummary,
    getVideoSummaries,
    primeVideoSummaries,
    isVideoSummaryMissing,
    markVideoSummariesMissing,
    getSearchRuntimeCatalog,
    primeSearchRuntimeCatalog,
    requestPreviewGrant,
  }), [getScrollPosition, getSearchRuntimeCatalog, getVideoSummaries, getVideoSummary, isVideoSummaryMissing, markVideoSummariesMissing, panel, primeSearchRuntimeCatalog, primeVideoSummaries, requestPreviewGrant, searchCanvas, setScrollPosition, setTileSize, tileSize, view]);

  return (
    <SearchWorkspaceContext.Provider value={value}>
      <PreviewGrantProvider requestPreviewGrant={requestPreviewGrant}>{children}</PreviewGrantProvider>
    </SearchWorkspaceContext.Provider>
  );
};

export const useSearchWorkspace = (): SearchWorkspaceContextValue => {
  const value = useContext(SearchWorkspaceContext);
  if (!value) {
    throw new Error('useSearchWorkspace must be used within SearchWorkspaceProvider');
  }
  return value;
};
